import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  getUniformesResumen,
  getUniformesV,
  updateAsigU,
  updateStockU,
} from "../../services/uniformes.services";
import { getApiImagen } from "../../utils/getApiBaseUrl";
import ModalAsignacionUniforme from "../../modal/asignacionuniformes/modaluniformeasig";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import ModalStockminU from "../../modal/stockminUniformes";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

const COLOR_WHEEL = [
  { color: "#378ADD", bg: "#E6F1FB" },
  { color: "#3B6D11", bg: "#EAF3DE" },
  { color: "#854F0B", bg: "#FAEEDA" },
  { color: "#A32D2D", bg: "#FCEBEB" },
  { color: "#533AB7", bg: "#EEEDFE" },
  { color: "#0F6E56", bg: "#E1F5EE" },
  { color: "#993556", bg: "#FBEAF0" },
  { color: "#5F5E5A", bg: "#F1EFE8" },
];

const estadoColorCache = {};

const getEstadoColor = (estado) => {
  if (estadoColorCache[estado]) return estadoColorCache[estado];
  const idx = Object.keys(estadoColorCache).length % COLOR_WHEEL.length;
  estadoColorCache[estado] = COLOR_WHEEL[idx];
  return estadoColorCache[estado];
};

const formatLabel = (estado) =>
  estado
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

// ── Columnas del DataGrid ─────────────────────────────────────────────────────
const buildColumns = ({ rows, onAsignar, onRetirar, onDevolver }) => {
  const tieneEnUso = rows.some(
    (r) => r.estado?.toUpperCase().replace(/ /g, "_") === "EN_USO",
  );

  const tienePerdida = rows.some((r) => {
    const estado = r.estado?.toUpperCase().replace(/ /g, "_");
    return estado === "PERDIDA" || estado === "PÉRDIDA";
  });

  const tieneDeBaja = rows.some(
    (r) => r.estado?.toUpperCase().replace(/ /g, "_") === "DE_BAJA",
  );

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 70,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          #{params.value}
        </Typography>
      ),
    },

    {
      field: "descripcion",
      headerName: "Descripción",
      flex: 0.5,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },

    {
      field: "talla",
      headerName: "Talla",
      width: 80,
    },

    {
      field: "empresa",
      headerName: "Empresa",
      flex: 0.5,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          {params.value}
        </Typography>
      ),
    },

    {
      field: "estado",
      headerName: "Estado",
      width: 130,
      renderCell: (params) => {
        const { color, bg } = getEstadoColor(params.value);

        return (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              background: bg,
              border: `1px solid ${color}33`,
              borderRadius: "20px",
              px: 1.25,
              py: 0.25,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: color,
                flexShrink: 0,
              }}
            />

            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color,
                letterSpacing: "0.04em",
              }}
            >
              {formatLabel(params.value)}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: "fecha",
      headerName: "Fecha recepción",
      width: 150,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          {params.value
            ? new Date(params.value).toLocaleDateString("es-PY")
            : "—"}
        </Typography>
      ),
    },
  ];

  // 🔵 SOLO SI HAY EN USO
  if (tieneEnUso) {
    columns.push(
      {
        field: "usuario",
        headerName: "Utilizado por",
        flex: 1,
        renderCell: (params) => {
          const estado = params.row.estado?.toUpperCase().replace(/ /g, "_");

          if (estado !== "EN_USO") return null;

          return (
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              {params.value || "—"}
            </Typography>
          );
        },
      },

      {
        field: "fecha_entrega",
        headerName: "Fecha entrega",
        width: 120,
        renderCell: (params) => {
          const estado = params.row.estado?.toUpperCase().replace(/ /g, "_");

          if (estado !== "EN_USO") return null;

          return (
            <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
              {params.value
                ? new Date(params.value).toLocaleDateString("es-PY")
                : "—"}
            </Typography>
          );
        },
      },

      {
        field: "estado_entrega",
        headerName: "Estado entrega",
        width: 130,
        renderCell: (params) => {
          const estado = params.row.estado?.toUpperCase().replace(/ /g, "_");

          if (estado !== "EN_USO") return null;

          return (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                background: "rgba(99,179,237,0.1)",
                border: "1px solid rgba(99,179,237,0.3)",
                borderRadius: "20px",
                px: 1.25,
                py: 0.25,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#63b3ed",
                  flexShrink: 0,
                }}
              />

              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#63b3ed",
                  letterSpacing: "0.04em",
                }}
              >
                {params.value ? formatLabel(params.value) : "—"}
              </Typography>
            </Box>
          );
        },
      },
    );
  }

  // 🟠 SOLO SI HAY PÉRDIDA
  if (tienePerdida) {
    columns.push({
      field: "obs_st",
      headerName: "Observación",
      flex: 1,
      renderCell: (params) => {
        const estado = params.row.estado?.toUpperCase().replace(/ /g, "_");

        if (estado !== "PERDIDA" && estado !== "PÉRDIDA") return null;

        return (
          <Typography
            sx={{
              fontSize: "12px",
              color: "#f6ad55",
              fontStyle: "italic",
            }}
          >
            {params.value || "—"}
          </Typography>
        );
      },
    });
  }

  // 📷 Imagen
  columns.push({
    field: "img",
    headerName: "Imagen",
    flex: 0.6,
    headerAlign: "left",
    align: "left",

    renderCell: (params) => (
      <img
        src={`${getApiImagen()}${params.value}`}
        alt="Uniforme"
        style={{
          width: "150px",
          height: "100px",
          objectFit: "cover",
        }}
      />
    ),
  });

  // ⚙️ Acciones
  columns.push({
    field: "acciones",
    headerName: "Acciones",
    width: 200,
    sortable: false,
    filterable: false,

    renderCell: (params) => {
      const estado = params.row.estado;

      const bloqueado =
        estado === "DE BAJA" || estado === "PERDIDA" || estado === "PÉRDIDA";

      // 🔒 Bloqueado
      if (bloqueado) {
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.5,
                py: 0.5,
                borderRadius: "8px",
                border: "1px solid rgba(100,100,100,0.3)",
                color: "rgba(150,150,150,0.6)",
                fontSize: "11px",
                fontWeight: 600,
                cursor: "default",
              }}
            >
              <LockIcon
                sx={{
                  color: "#ffa726",
                  fontSize: 22,
                  filter: "drop-shadow(0 0 6px rgba(255,167,38,0.8))",
                }}
              />
            </Box>
          </Box>
        );
      }

      // 🔵 EN USO
      if (estado === "EN USO" || estado === "EN_USO") {
        return (
          <Box display="flex" gap={0.75} alignItems="center" height="100%">
            <Button
              size="small"
              onClick={() => onDevolver(params.row)}
              sx={{
                minWidth: 0,
                px: 1.5,
                py: 0.5,
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "none",
                border: "1px solid rgba(99,179,237,0.5)",
                borderRadius: "8px",
                color: "#63b3ed",
                "&:hover": {
                  background: "rgba(99,179,237,0.1)",
                },
              }}
            >
              Devolver
            </Button>
          </Box>
        );
      }

      // 🟡 NUEVO
      if (estado === "NUEVO") {
        return (
          <Box display="flex" gap={0.75} alignItems="center" height="100%">
            <Button
              size="small"
              onClick={() => onAsignar(params.row)}
              sx={{
                minWidth: 0,
                px: 1.25,
                py: 0.5,
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "none",
                border: "1px solid rgba(206,220,0,0.4)",
                borderRadius: "8px",
                color: "rgb(180,190,0)",
                "&:hover": {
                  background: "rgba(206,220,0,0.08)",
                },
              }}
            >
              Asignar
            </Button>
            <Button
              size="small"
              onClick={() => onRetirar(params.row)}
              sx={{
                minWidth: 0,
                px: 1.25,
                py: 0.5,
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "none",
                border: "1px solid rgba(226,75,74,0.5)",
                borderRadius: "8px",
                color: "#e24b4a",
                "&:hover": {
                  background: "rgba(226,75,74,0.1)",
                },
              }}
            >
              Retirar
            </Button>
          </Box>
        );
      }

      // 🟢 DEVUELTO
      if (estado === "DEVUELTO") {
        return (
          <Box display="flex" gap={0.75} alignItems="center" height="100%">
            <Button
              size="small"
              onClick={() => onAsignar(params.row)}
              sx={{
                minWidth: 0,
                px: 1.25,
                py: 0.5,
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "none",
                border: "1px solid rgba(206,220,0,0.4)",
                borderRadius: "8px",
                color: "rgb(180,190,0)",
                "&:hover": {
                  background: "rgba(206,220,0,0.08)",
                },
              }}
            >
              Asignar
            </Button>

            <Button
              size="small"
              onClick={() => onRetirar(params.row)}
              sx={{
                minWidth: 0,
                px: 1.25,
                py: 0.5,
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "none",
                border: "1px solid rgba(226,75,74,0.5)",
                borderRadius: "8px",
                color: "#e24b4a",
                "&:hover": {
                  background: "rgba(226,75,74,0.1)",
                },
              }}
            >
              Retirar
            </Button>
          </Box>
        );
      }

      return null;
    },
  });

  return columns;
};

// ── Card de estado ────────────────────────────────────────────────────────────
const EstadoCard = ({ estado, cantidad, isActive, onClick }) => {
  const { color, bg } = getEstadoColor(estado);
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      sx={{
        flex: "1 1 120px",
        minWidth: 110,
        background: isActive ? bg : "transparent",
        border: isActive
          ? `2px solid ${color}`
          : "1.5px solid rgba(128,128,128,0.2)",
        borderRadius: "14px",
        padding: "14px 16px",
        cursor: "pointer",
        transition: "all 0.18s ease",
        outline: "none",
        "&:hover": {
          background: bg,
          borderColor: color,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "10px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: isActive ? color : "text.secondary",
          mb: 0.5,
        }}
      >
        {formatLabel(estado)}
      </Typography>
      <Typography
        sx={{
          fontSize: "28px",
          fontWeight: 700,
          color: isActive ? color : "text.primary",
          lineHeight: 1,
        }}
      >
        {parseInt(cantidad).toLocaleString("es-PY")}
      </Typography>
      <Typography sx={{ fontSize: "11px", color: "text.secondary", mt: 0.4 }}>
        unidades
      </Typography>
    </Box>
  );
};

// ── Modal Retirar Uniforme ────────────────────────────────────────────────────
const ModalRetirarUniforme = ({ open, onClose, stock, onSuccess }) => {
  const [observacion, setObservacion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setObservacion("");
    setError("");
    onClose();
  };

  const handleRetirar = async () => {
    if (observacion.trim().length < 5) {
      setError("La observación debe tener al menos 5 caracteres.");
      return;
    }
    setLoading(true);
    try {
      await updateStockU(stock.id, {
        obs: observacion.trim(),
      });
      onSuccess?.();
      handleClose();
    } catch (err) {
      setError("Error al retirar el uniforme. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start", // 👈 alinea desde arriba, igual que el otro
        },
      }}
      PaperProps={{
        sx: {
          background: "rgb(36, 43, 67)",
          border: "1px solid rgba(226,75,74,0.3)",
          borderRadius: "16px",
          minWidth: 420,
          boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
          mt: "8%",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 1,
          color: "#e24b4a",
          fontWeight: 700,
          fontSize: "20px",
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            background: "rgba(226,75,74,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
          }}
        >
          ⚠️
        </Box>
        Retirar Uniforme
        <IconButton
          onClick={handleClose}
          sx={{
            ml: "auto",
            color: "rgba(255,255,255,0.4)",
            "&:hover": { color: "#fff" },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Info del uniforme */}
        {stock && (
          <Box
            sx={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "10px",
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                color: "rgba(255,255,255,0.5)",
                mb: 0.25,
              }}
            >
              #{stock.id} · {stock.talla}
            </Typography>
            <Typography
              sx={{ fontSize: "18px", color: "#e2e8f0", fontWeight: 600 }}
            >
              {stock.descripcion}
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                color: "rgba(255,255,255,0.4)",
                mt: 0.25,
              }}
            >
              {stock.empresa}
            </Typography>
          </Box>
        )}

        {/* Campo observación */}
        <Typography
          sx={{
            fontSize: "16px",
            color: "rgba(255,255,255,0.6)",
            mb: 0.75,
            fontWeight: 600,
          }}
        >
          Motivo / Observación <span style={{ color: "#e24b4a" }}>*</span>
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          value={observacion}
          onChange={(e) => {
            setObservacion(e.target.value);
            if (error) setError("");
          }}
          placeholder="Ej: Uniforme dañado por desgaste excesivo..."
          inputProps={{ minLength: 5 }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "15px",
              color: "#e2e8f0",
              background: "rgba(255,255,255,0.04)",
              borderRadius: "10px",
              "& fieldset": {
                borderColor: error ? "#e24b4a" : "rgba(255,255,255,0.12)",
              },
              "&:hover fieldset": {
                borderColor: error ? "#e24b4a" : "rgba(255,255,255,0.25)",
              },
              "&.Mui-focused fieldset": {
                borderColor: error ? "#e24b4a" : "rgba(226,75,74,0.6)",
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255,255,255,0.25)",
            },
          }}
        />

        {/* Contador y error */}
        <Box display="flex" justifyContent="space-between" mt={0.5}>
          {error ? (
            <Typography sx={{ fontSize: "11px", color: "#e24b4a" }}>
              {error}
            </Typography>
          ) : (
            <Box />
          )}
          <Typography
            sx={{
              fontSize: "11px",
              color:
                observacion.trim().length >= 5
                  ? "rgba(100,220,100,0.7)"
                  : "rgba(255,255,255,0.3)",
            }}
          >
            {observacion.trim().length} / mín. 5
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          sx={{
            background: "rgba(255,167,38,0.15)", // fondo suave difuminado
            color: "#ffa726",
            fontSize: "13px",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: 3,

            "&:hover": {
              background: "rgba(255,167,38,0.25)",
              boxShadow: "0 0 12px rgba(255,167,38,0.8)",
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleRetirar}
          disabled={loading || observacion.trim().length < 5}
          sx={{
            background:
              observacion.trim().length >= 5
                ? "#e24b4a"
                : "rgba(226,75,74,0.3)",
            color: "#fff",
            fontSize: "13px",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "10px",
            px: 3,
            "&:hover": { background: "#c73a39" },
            "&.Mui-disabled": {
              background: "rgba(226,75,74,0.2)",
              color: "rgba(255,255,255,0.3)",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={16} sx={{ color: "#fff" }} />
          ) : (
            "Confirmar retiro"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ModalDevolverUniforme = ({ open, onClose, stock, onSuccess }) => {
  const [estadoDevolucion, setEstadoDevolucion] = useState(null);
  const [observacion, setObservacion] = useState("");
  const [fechaDevolucion, setFechaDevolucion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setEstadoDevolucion(null);
    setObservacion("");
    setFechaDevolucion("");
    setError("");
    onClose();
  };

  const obsObligatoria = (() => {
    if (!stock?.fecha_entrega) return false;
    const entrega = new Date(stock.fecha_entrega);
    const hoy = new Date();
    const diffMeses =
      (hoy.getFullYear() - entrega.getFullYear()) * 12 +
      (hoy.getMonth() - entrega.getMonth());
    return diffMeses < 6;
  })();

const handleDevolver = async () => {
  if (!estadoDevolucion) {
    setError("Seleccioná el estado de devolución.");
    return;
  }
  if (!fechaDevolucion) {
    setError("La fecha de devolución es obligatoria.");
    return;
  }
  if (obsObligatoria && observacion.trim().length < 5) {
    setError(
      "La observación es obligatoria (mín. 5 caracteres) antes de los 6 meses de uso.",
    );
    return;
  }

  const payload = {
    estado_devolucion: estadoDevolucion,
    fecha_devolucion: fechaDevolucion,
    obs: observacion.trim() || null,
  };


  setLoading(true);
  try {
    await updateAsigU(stock.id, payload);
    onSuccess?.();
    handleClose();
  } catch (err) {
    console.error("❌ Error en handleDevolver:", err);
    setError("Error al registrar la devolución. Intente nuevamente.");
  } finally {
    setLoading(false);
  }
};


  const btnEstadoSx = (valor, colorActivo, bgActivo, borderActivo) => ({
    flex: 1,
    py: 1.1,
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "none",
    borderRadius: "10px",
    border:
      estadoDevolucion === valor
        ? `1.5px solid ${borderActivo}`
        : "1.5px solid rgba(255,255,255,0.1)",
    background:
      estadoDevolucion === valor ? bgActivo : "rgba(255,255,255,0.03)",
    color: estadoDevolucion === valor ? colorActivo : "rgba(255,255,255,0.4)",
    transition: "all 0.18s ease",
    "&:hover": {
      background: bgActivo,
      borderColor: borderActivo,
      color: colorActivo,
    },
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
      PaperProps={{
        sx: {
          background: "rgb(36, 43, 67)",
          border: "1px solid rgba(99,179,237,0.3)",
          borderRadius: "16px",
          minWidth: 420,
          boxShadow: "0 24px 48px rgba(0,0,0,0.6)",
          mt: "8%",
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 1,
          color: "#63b3ed",
          fontWeight: 700,
          fontSize: "16px",
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "8px",
            background: "rgba(99,179,237,0.15)",
            border: "1px solid rgba(99,179,237,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "17px",
          }}
        >
          🔄
        </Box>
        Devolver Uniforme
        <IconButton
          onClick={handleClose}
          sx={{
            ml: "auto",
            color: "rgba(255,255,255,0.35)",
            "&:hover": { color: "#fff", background: "rgba(255,255,255,0.08)" },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Info del uniforme */}
        {stock && (
          <Box
            sx={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "10px",
              p: 1.75,
              mb: 2.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                color: "rgba(255,255,255,0.4)",
                mb: 0.3,
                letterSpacing: "0.04em",
              }}
            >
              #{stock.id} · Talla {stock.talla}
            </Typography>
            <Typography
              sx={{ fontSize: "14px", color: "#e2e8f0", fontWeight: 600 }}
            >
              {stock.descripcion}
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "rgba(255,255,255,0.4)", mt: 0.3 }}
            >
              {stock.empresa}
            </Typography>

            {/* Fila de datos: usuario, fecha entrega, estado entrega */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 1.25,
                pt: 1.25,
                borderTop: "1px solid rgba(255,255,255,0.07)",
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "10px",
                    color: "rgba(255,255,255,0.35)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  Utilizado por
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#e2e8f0",
                    fontWeight: 600,
                    mt: 0.2,
                  }}
                >
                  {stock.usuario || "—"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "10px",
                    color: "rgba(255,255,255,0.35)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  Fecha entrega
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#e2e8f0",
                    fontWeight: 600,
                    mt: 0.2,
                  }}
                >
                  {stock.fecha_entrega
                    ? new Date(stock.fecha_entrega).toLocaleDateString("es-PY")
                    : "—"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "10px",
                    color: "rgba(255,255,255,0.35)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  Estado entrega
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#63b3ed",
                    fontWeight: 600,
                    mt: 0.2,
                  }}
                >
                  {stock.estado_entrega
                    ? formatLabel(stock.estado_entrega)
                    : "—"}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Aviso obs obligatoria */}
        {obsObligatoria && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              background: "rgba(246,173,85,0.1)",
              border: "1px solid rgba(246,173,85,0.3)",
              borderRadius: "9px",
              px: 1.5,
              py: 1,
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: "17px", lineHeight: 1 }}>⚠️</Typography>
            <Typography
              sx={{ fontSize: "11px", color: "#f6ad55", lineHeight: 1.4 }}
            >
              Faltan menos de 6 meses desde la entrega. La observación es{" "}
              <strong>obligatoria</strong>.
            </Typography>
          </Box>
        )}

        {/* Botones estado devolución */}
        <Typography
          sx={{
            fontSize: "12px",
            color: "rgba(255,255,255,0.6)",
            mb: 1,
            fontWeight: 600,
            letterSpacing: "0.03em",
          }}
        >
          Estado de devolución <span style={{ color: "#e24b4a" }}>*</span>
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
          <Button
            onClick={() => {
              setEstadoDevolucion("UTILIZABLE");
              if (error) setError("");
            }}
            sx={btnEstadoSx(
              "UTILIZABLE",
              "#68d391",
              "rgba(104,211,145,0.12)",
              "#68d391",
            )}
          >
            ✓ Utilizable
          </Button>
          <Button
            onClick={() => {
              setEstadoDevolucion("NO UTILIZABLE");
              if (error) setError("");
            }}
            sx={btnEstadoSx(
              "NO UTILIZABLE",
              "#e24b4a",
              "rgba(226,75,74,0.12)",
              "#e24b4a",
            )}
          >
            ✕ No utilizable
          </Button>
        </Box>

        {/* Fecha devolución */}
        <Typography
          sx={{
            fontSize: "12px",
            color: "rgba(255,255,255,0.6)",
            mb: 0.75,
            fontWeight: 600,
            letterSpacing: "0.03em",
          }}
        >
          Fecha de devolución <span style={{ color: "#e24b4a" }}>*</span>
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <DatePicker
            value={fechaDevolucion ? dayjs(fechaDevolucion) : null}
            onChange={(newValue) => {
              setFechaDevolucion(newValue ? newValue.format("YYYY-MM-DD") : "");
              if (error) setError("");
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!error && !fechaDevolucion,
                helperText: error,
                sx: {
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "13px",
                    color: "#e2e8f0",
                    background: "rgba(255,255,255,0.04)",
                    borderRadius: "10px",
                    "& fieldset": {
                      borderColor:
                        error && !fechaDevolucion
                          ? "#e24b4a"
                          : "rgba(255,255,255,0.12)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.22)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(99,179,237,0.55)",
                    },
                  },
                },
              },
            }}
          />
        </LocalizationProvider>

        {/* Observación */}
        <Typography
          sx={{
            fontSize: "12px",
            color: "rgba(255,255,255,0.6)",
            mb: 0.75,
            fontWeight: 600,
            letterSpacing: "0.03em",
          }}
        >
          Observación{" "}
          {obsObligatoria ? (
            <span style={{ color: "#e24b4a" }}>* (obligatoria)</span>
          ) : (
            <span style={{ color: "rgba(255,255,255,0.3)" }}>(opcional)</span>
          )}
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          value={observacion}
          onChange={(e) => {
            setObservacion(e.target.value);
            if (error) setError("");
          }}
          placeholder="Ej: Uniforme devuelto en buen estado..."
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "13px",
              color: "#e2e8f0",
              background: "rgba(255,255,255,0.04)",
              borderRadius: "10px",
              "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
              "&:hover fieldset": { borderColor: "rgba(255,255,255,0.22)" },
              "&.Mui-focused fieldset": {
                borderColor: "rgba(99,179,237,0.55)",
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255,255,255,0.22)",
              fontSize: "13px",
            },
          }}
        />

        {/* Error + contador */}
        <Box
          display="flex"
          justifyContent="space-between"
          mt={0.75}
          alignItems="center"
        >
          {error ? (
            <Typography sx={{ fontSize: "11px", color: "#e24b4a" }}>
              {error}
            </Typography>
          ) : (
            <Box />
          )}
          {obsObligatoria && (
            <Typography
              sx={{
                fontSize: "11px",
                color:
                  observacion.trim().length >= 5
                    ? "rgba(104,211,145,0.8)"
                    : "rgba(255,255,255,0.25)",
                transition: "color 0.2s",
              }}
            >
              {observacion.trim().length} / mín. 5
            </Typography>
          )}
        </Box>
      </DialogContent>

      {/* ACCIONES */}
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          sx={{
            background: "rgba(255,167,38,0.15)",
            color: "#ffa726",
            fontSize: "13px",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: 3,
            "&:hover": {
              background: "rgba(255,167,38,0.25)",
              boxShadow: "0 0 12px rgba(255,167,38,0.4)",
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleDevolver}
          disabled={loading}
          sx={{
            background:
              estadoDevolucion && fechaDevolucion
                ? "#63b3ed"
                : "rgba(99,179,237,0.2)",
            color:
              estadoDevolucion && fechaDevolucion
                ? "#1a2035"
                : "rgba(255,255,255,0.25)",
            fontSize: "13px",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "10px",
            px: 3,
            py: 0.9,
            transition: "background 0.2s",
            "&:hover": {
              background:
                estadoDevolucion && fechaDevolucion
                  ? "#4a9fd4"
                  : "rgba(99,179,237,0.2)",
            },
            "&.Mui-disabled": {
              background: "rgba(99,179,237,0.15)",
              color: "rgba(255,255,255,0.2)",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={16} sx={{ color: "#1a2035" }} />
          ) : (
            "Confirmar devolución"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────
const StockUniformesV = () => {
  const [resumen, setResumen] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [activeEstado, setActiveEstado] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [loadingRegistros, setLoadingRegistros] = useState(false);
  const [openAsignacion, setOpenAsignacion] = useState(false);
  const [stockSeleccionado, setStockSeleccionado] = useState(null);
  const [openRetirar, setOpenRetirar] = useState(false);
  const [stockParaRetirar, setStockParaRetirar] = useState(null);
  const [openDevolver, setOpenDevolver] = useState(false);
  const [stockParaDevolver, setStockParaDevolver] = useState(null);
  const [estadosSeleccionados, setEstadosSeleccionados] = useState([
    "NUEVO",
    "DEVUELTO",
  ]);
  const [modalOpen, setModalOpen] = useState(true);
  const estadosDisponibles = resumen.map((r) => r.estado);
  const fetchResumen = useCallback(async () => {
    setLoadingResumen(true);
    try {
      const data = await getUniformesResumen();
      setResumen(data);
    } catch (err) {
      console.error("Error al cargar resumen:", err);
    } finally {
      setLoadingResumen(false);
    }
  }, []);

  const exportarExcel = async () => {
    const workbook = new ExcelJS.Workbook();

    // 🔹 HOJA GENERAL (RESUMEN)
    const resumenSheet = workbook.addWorksheet("Resumen");
    resumenSheet.columns = [
      { header: "Estado", key: "estado", width: 20 },
      { header: "Cantidad", key: "cantidad", width: 15 },
    ];

    resumenConStock.forEach((r) => {
      resumenSheet.addRow({ estado: r.estado, cantidad: r.cantidad });
    });

    // Estilo para la cabecera de Resumen
    resumenSheet.getRow(1).eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF3E4396" }, // RGB: 62, 67, 150 -> HEX: 3E4396
      };
      cell.font = { color: { argb: "FFFFFFFF" }, bold: true };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });

    // 🔹 HOJAS DINÁMICAS POR ESTADO
    resumen.forEach((estadoItem) => {
      const estado = estadoItem.estado;
      const estadoKey = estado?.toUpperCase().replace(/ /g, "_");

      const registrosEstado = registros.filter((r) => r.estado === estado);

      // Creamos la pestaña limitando a 31 caracteres
      const sheet = workbook.addWorksheet(estado.substring(0, 31));

      // Definimos columnas base comunes para todos los estados
      const columnasDef = [
        { header: "ID", key: "id", width: 10 },
        { header: "Descripción", key: "descripcion", width: 30 },
        { header: "Talla", key: "talla", width: 10 },
        { header: "Empresa", key: "empresa", width: 20 },
        { header: "Estado", key: "estado", width: 15 },
        { header: "Fecha", key: "fecha", width: 15 },
      ];

      // Condicionales de columnas según el estado actual (Igual a tu buildColumns)
      if (estadoKey === "EN_USO") {
        columnasDef.push(
          { header: "Utilizado por", key: "usuario", width: 25 },
          { header: "Fecha entrega", key: "fecha_entrega", width: 15 },
          { header: "Estado entrega", key: "estado_entrega", width: 18 },
        );
      }

      if (estadoKey === "PERDIDA" || estadoKey === "PÉRDIDA") {
        columnasDef.push({ header: "Observación", key: "obs_st", width: 30 });
      }

      if (estadoKey === "DE_BAJA") {
        columnasDef.push({ header: "Observación", key: "obs", width: 30 });
      }

      sheet.columns = columnasDef;

      // Mapeamos e insertamos los datos respetando las columnas activas
      registrosEstado.forEach((r) => {
        const rowData = {
          id: r.id,
          descripcion: r.descripcion,
          talla: r.talla,
          empresa: r.empresa,
          estado: r.estado,
          fecha: r.fecha ? new Date(r.fecha).toLocaleDateString("es-PY") : "-",
        };

        if (estadoKey === "EN_USO") {
          rowData.usuario = r.usuario || "-";
          rowData.fecha_entrega = r.fecha_entrega
            ? new Date(r.fecha_entrega).toLocaleDateString("es-PY")
            : "-";
          rowData.estado_entrega = r.estado_entrega || "-";
        }
        if (estadoKey === "PERDIDA" || estadoKey === "PÉRDIDA") {
          rowData.obs_st = r.obs_st || "-";
        }
        if (estadoKey === "DE_BAJA") {
          rowData.obs = r.obs || "-";
        }

        sheet.addRow(rowData);
      });

      // Aplicamos los estilos solicitados a la cabecera (Fila 1) de esta pestaña
      sheet.getRow(1).eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF3E4396" }, // RGB (62, 67, 150)
        };
        cell.font = { color: { argb: "FFFFFFFF" }, bold: true };
        cell.alignment = { vertical: "middle", horizontal: "left" };
      });
    });

    // 💾 DESCARGA DEL BUFFER
    const buffer = await workbook.xlsx.writeBuffer();
    const fileData = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(fileData, "Resumen_Uniformes.xlsx");
  };

  const exportarPDF = () => {
    const doc = new jsPDF("landscape");

    // 🔹 RESUMEN GENERAL
    doc.setFontSize(18);
    doc.text("Resumen General Uniformes", 14, 15);

    autoTable(doc, {
      startY: 25,
      head: [["Estado", "Cantidad"]],
      body: resumenConStock.map((r) => [r.estado, r.cantidad]),
      headStyles: {
        fillColor: [62, 67, 150],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
    });

    // 🔹 TABLAS POR ESTADO
    resumen.forEach((estadoItem) => {
      const estado = estadoItem.estado;
      const estadoKey = estado?.toUpperCase().replace(/ /g, "_");

      const registrosEstado = registros.filter((r) => r.estado === estado);

      doc.addPage("landscape");
      doc.setFontSize(18);
      doc.text(`Estado: ${estado}`, 14, 15);

      // Cabeceras base comunes
      const columnasHead = [
        "ID",
        "Descripcion",
        "Talla",
        "Empresa",
        "Estado",
        "Fecha",
      ];

      // Agregar cabeceras según el estado
      if (estadoKey === "EN_USO") {
        columnasHead.push("Utilizado por", "Fecha Entrega", "Estado Entrega");
      } else if (
        estadoKey === "PERDIDA" ||
        estadoKey === "PÉRDIDA" ||
        estadoKey === "DE_BAJA"
      ) {
        columnasHead.push("Observacion");
      }

      // Mapear el cuerpo de la tabla dinámicamente basándonos en el mismo filtro de cabeceras
      const columnasBody = registrosEstado.map((r) => {
        const row = [
          r.id,
          r.descripcion,
          r.talla,
          r.empresa,
          r.estado,
          r.fecha ? new Date(r.fecha).toLocaleDateString("es-PY") : "-",
        ];

        if (estadoKey === "EN_USO") {
          row.push(
            r.usuario || "-",
            r.fecha_entrega
              ? new Date(r.fecha_entrega).toLocaleDateString("es-PY")
              : "-",
            r.estado_entrega || "-",
          );
        } else if (estadoKey === "PERDIDA" || estadoKey === "PÉRDIDA") {
          row.push(r.obs_st || "-");
        } else if (estadoKey === "DE_BAJA") {
          row.push(r.obs || "-");
        }

        return row;
      });

      autoTable(doc, {
        startY: 25,
        head: [columnasHead],
        body: columnasBody,
        headStyles: {
          fillColor: [62, 67, 150],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        styles: {
          fontSize: 8,
        },
      });
    });

    doc.save("Resumen_Uniformes.pdf");
  };
  const calcularStock = () => {
    return resumen
      .filter((r) => estadosSeleccionados.includes(r.estado))
      .reduce((acc, r) => acc + Number(r.cantidad), 0);
  };

  const resumenConStock = [
    ...resumen,
    {
      id: "stock",
      estado: "EN STOCK",
      cantidad: calcularStock(),
    },
  ];
  const fetchRegistros = useCallback(async () => {
    setLoadingRegistros(true);
    try {
      const data = await getUniformesV();
      setRegistros(data);
    } catch (err) {
      console.error("Error al cargar registros:", err);
    } finally {
      setLoadingRegistros(false);
    }
  }, []);

  useEffect(() => {
    fetchResumen();
    fetchRegistros();
  }, [fetchResumen, fetchRegistros]);

  const total = resumen.reduce((acc, r) => acc + parseInt(r.cantidad || 0), 0);

  // Filtra las filas del DataGrid según el estado activo
  const rowsFiltradas =
    activeEstado === null
      ? [] // 👈 General NO muestra registros
      : registros.filter((r) => r.estado === activeEstado);

  const handleCardClick = (estado) =>
    setActiveEstado((prev) => (prev === estado ? null : estado));

  const columns = buildColumns({
    rows: rowsFiltradas,

    onAsignar: (row) => {
      setStockSeleccionado(row);
      setOpenAsignacion(true);
    },

    onRetirar: (row) => {
      setStockParaRetirar(row);
      setOpenRetirar(true);
    },

    onDevolver: (row) => {
      setStockParaDevolver(row);
      setOpenDevolver(true);
    },
  });

  return (
    <Box m="20px">
      <Header
        title="Stock de Uniformes"
        subtitle="Resumen general por estado"
      />
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 2,
        }}
      >
        {/* PDF */}
        <Button
          onClick={exportarPDF}
          sx={{
            border: "1px solid #e53935",
            color: "#e53935",
            borderRadius: "10px",
            px: 2,
            py: 1,
            textTransform: "none",
            fontWeight: 600,

            "&:hover": {
              background: "rgba(229,57,53,0.08)",
            },
          }}
        >
          <img
            src="https://cdn-icons-png.flaticon.com/512/337/337946.png"
            alt="PDF Icon"
            style={{
              width: 29,
              height: 29,
              marginRight: 8,
            }}
          />
          Exportar PDF
        </Button>

        {/* EXCEL */}
        <Button
          onClick={exportarExcel}
          sx={{
            border: "1px solid #2e7d32",
            color: "#2e7d32",
            borderRadius: "10px",
            px: 2,
            py: 1,
            textTransform: "none",
            fontWeight: 600,

            "&:hover": {
              background: "rgba(46,125,50,0.08)",
            },
          }}
        >
          <img
            src="https://cdn-icons-png.flaticon.com/512/732/732220.png"
            alt="Excel Icon"
            style={{
              width: 24,
              height: 24,
              marginRight: 8,
            }}
          />
          Exportar Excel
        </Button>
      </Box>
      {loadingResumen ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress sx={{ color: "rgb(206,220,0)" }} />
        </Box>
      ) : (
        <>
          {/* ── Fila de cards horizontales ──────────────────────────────── */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              mb: 3,
              alignItems: "stretch",
            }}
          >
            {/* Card General */}
            <Box
              onClick={() => setActiveEstado(null)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setActiveEstado(null)}
              sx={{
                flex: "1 1 120px",
                minWidth: 110,
                background:
                  activeEstado === null ? "rgba(206,220,0,0.1)" : "transparent",
                border:
                  activeEstado === null
                    ? "2px solid rgb(206,220,0)"
                    : "1.5px solid rgba(128,128,128,0.2)",
                borderRadius: "14px",
                padding: "14px 16px",
                cursor: "pointer",
                transition: "all 0.18s ease",
                outline: "none",
                "&:hover": {
                  background: "rgba(206,220,0,0.07)",
                  borderColor: "rgb(206,220,0)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color:
                    activeEstado === null ? "rgb(180,190,0)" : "text.secondary",
                  mb: 0.5,
                }}
              >
                General
              </Typography>
              <Typography
                sx={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color:
                    activeEstado === null ? "rgb(206,220,0)" : "text.primary",
                  lineHeight: 1,
                }}
              >
                {total.toLocaleString("es-PY")}
              </Typography>
              <Typography
                sx={{ fontSize: "14px", color: "text.secondary", mt: 0.4 }}
              >
                todas las unidades
              </Typography>
            </Box>

            {/* Cards dinámicos por estado */}
            {resumen.map((item) => (
              <Box
                key={item.estado}
                onClick={() => handleCardClick(item.estado)}
                sx={{
                  flex: "1 1 120px",
                  minWidth: 110,
                  background:
                    activeEstado === item.estado
                      ? "rgba(62,67,150,0.1)"
                      : "transparent",
                  border:
                    activeEstado === item.estado
                      ? "2px solid #3e4396"
                      : "1.5px solid rgba(128,128,128,0.2)",
                  borderRadius: "14px",
                  padding: "14px 16px",
                  cursor: "pointer",
                  transition: "all 0.18s ease",
                  "&:hover": {
                    background: "rgba(62,67,150,0.07)",
                    borderColor: "#3e4396",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color:
                      activeEstado === item.estado
                        ? "#3e4396"
                        : "text.secondary",
                    mb: 0.5,
                  }}
                >
                  {item.estado}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "28px",
                    fontWeight: 700,
                    color:
                      activeEstado === item.estado ? "#3e4396" : "text.primary",
                    lineHeight: 1,
                  }}
                >
                  {item.cantidad}
                </Typography>
                <Typography
                  sx={{ fontSize: "14px", color: "text.secondary", mt: 0.4 }}
                >
                  unidades
                </Typography>
              </Box>
            ))}

            {/* Botón actualizar */}
            <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
              <Button
                onClick={() => {
                  fetchResumen();
                  fetchRegistros();
                }}
                sx={{
                  border: "1.5px solid rgb(206,220,0)",
                  color: "rgb(206,220,0)",
                  borderRadius: "12px",
                  px: 2.5,
                  height: "42px",
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  "&:hover": { background: "rgba(206,220,0,0.12)" },
                }}
              >
                ↻ Actualizar
              </Button>
            </Box>
          </Box>
          <ModalAsignacionUniforme
            open={openAsignacion}
            onClose={() => setOpenAsignacion(false)}
            stock={stockSeleccionado}
          />

          <ModalRetirarUniforme
            open={openRetirar}
            onClose={() => setOpenRetirar(false)}
            stock={stockParaRetirar}
            onSuccess={() => {
              fetchResumen();
              fetchRegistros();
            }}
          />

          <ModalDevolverUniforme
            open={openDevolver}
            onClose={() => setOpenDevolver(false)}
            stock={stockParaDevolver}
            onSuccess={() => {
              fetchResumen();
              fetchRegistros();
            }}
          />

          <ModalStockminU
            open={modalOpen}
            onClose={() => setModalOpen(false)}
          />
          {/* ── DataGrid ────────────────────────────────────────────────── */}
          {/* ── DataGrid ────────────────────────────────────────────────── */}
          {activeEstado === null ? (
            // 👇 Mini DataGrid SOLO para el resumen general
            <Box
              sx={{
                "& .MuiDataGrid-root": {
                  border: "0.5px solid rgba(128,128,128,0.2)",
                  borderRadius: "16px",
                  fontSize: "13px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  background: "rgba(128,128,128,0.04)",
                  borderBottom: "0.5px solid rgba(128,128,128,0.15)",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                },
              }}
            >
              <DataGrid
                rows={resumenConStock.map((r, i) => ({ id: i, ...r }))} // 👈 id obligatorio
                columns={[
                  { field: "estado", headerName: "Estado", flex: 1 },
                  { field: "cantidad", headerName: "Cantidad", flex: 1 },
                ]}
                autoHeight
                hideFooter // 👈 sin paginación, solo tabla rápida
                disableRowSelectionOnClick
                localeText={{ noRowsLabel: "Sin resumen disponible" }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  mt: 4,
                  mb: 3,
                }}
              >
                {estadosDisponibles.map((estado) => {
                  const seleccionado = estadosSeleccionados.includes(estado);
                  return (
                    <Box
                      key={estado}
                      onClick={() =>
                        setEstadosSeleccionados((prev) =>
                          seleccionado
                            ? prev.filter((e) => e !== estado)
                            : [...prev, estado],
                        )
                      }
                      sx={{
                        flex: "1 1 120px",
                        minWidth: 110,
                        background: seleccionado
                          ? "rgba(62,67,150,0.1)"
                          : "transparent",
                        border: seleccionado
                          ? "2px solid #3e4396"
                          : "1.5px solid rgba(128,128,128,0.2)",
                        borderRadius: "14px",
                        padding: "14px 16px",
                        cursor: "pointer",
                        transition: "all 0.18s ease",
                        "&:hover": {
                          background: "rgba(62,67,150,0.07)",
                          borderColor: "#3e4396",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "10px",
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: seleccionado ? "#ffa726" : "text.secondary",
                          mb: 0.5,
                        }}
                      >
                        {estado}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "20px",
                          fontWeight: 700,
                          color: seleccionado ? "#ffa726" : "text.primary",
                          lineHeight: 1,
                        }}
                      >
                        {resumen.find((r) => r.estado === estado)?.cantidad ??
                          0}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: seleccionado ? "#ffa726" : "text.primary",
                          mt: 0.4,
                        }}
                      >
                        unidades
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                Stock calculado: {calcularStock()}
              </Typography>
            </Box>
          ) : (
            // 👇 DataGrid completo para registros individuales
            <Box
              sx={{
                "& .MuiDataGrid-root": {
                  border: "0.5px solid rgba(128,128,128,0.2)",
                  borderRadius: "16px",
                },
              }}
            >
              <DataGrid
                rows={rowsFiltradas}
                columns={columns}
                loading={loadingRegistros}
                autoHeight
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                disableRowSelectionOnClick
                localeText={{
                  noRowsLabel: "Sin registros para este estado",
                  footerRowSelected: (count) =>
                    `${count} fila(s) seleccionada(s)`,
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default StockUniformesV;
